import { useEffect, useRef, useReducer } from "react";
import { reducer, getInitialState } from "./reducer";
import { ACTIONS } from "./constants";
import { Callback, Timeout, Timer } from "./types";

export const useTimer = (
  callback: Callback,
  timeout?: Timeout
): Timer | null => {
  // 1. Проверяем был ли указан таймаут при использовании useTimer.
  const timeoutIsNotSpecified = !timeout;

  const [state, dispatch] = useReducer(reducer, getInitialState(timeout));
  const timer = useRef<number>();

  const getTimeLeft = () => {
    const currentTimestamp = Date.now();
    const { startedAt, timeout } = state;

    if (timeout) {
      return timeout - (currentTimestamp - startedAt);
    }

    return timeout;
  };

  useEffect(() => {
    // 2. Не запускаем таймер при любом falsy-значении timeout, даже при 0 мс.
    // 3. Повторяем это условие здесь и далее, чтобы не писать обходные решения в стиле conditional hook.
    if (timeoutIsNotSpecified) {
      return () => {};
    }

    timer.current = window.setTimeout(callback, state.timeout);

    dispatch({ type: ACTIONS.START });

    return () => {
      window.clearTimeout(timer.current);
    };
  }, []);

  const pause = () => {
    if (timeoutIsNotSpecified) {
      return null;
    }

    window.clearTimeout(timer.current);

    dispatch({ type: ACTIONS.PAUSE, payload: getTimeLeft() });
  };

  const resume = () => {
    if (timeoutIsNotSpecified) {
      return null;
    }

    timer.current = window.setTimeout(callback, state.timeout);
    dispatch({ type: ACTIONS.START });
  };

  const stop = () => {
    if (timeoutIsNotSpecified) {
      return null;
    }

    if (typeof timer.current === "number") {
      clearTimeout(timer.current);
    }

    window.clearTimeout(timer.current);

    dispatch({ type: ACTIONS.STOP });
  };

  return { pause, resume, stop };
};
